import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { StripeCardElement } from "@stripe/stripe-js"
import './CheckOut.scss';
import axios from 'axios';
import { LANDING_URL, PURCHASE_PRICE, SERVER_URL } from '../../config';
import { Authorization } from '../../services/authorization';
import { Checkbox } from '@material-ui/core';

import { useAppSelector } from '../../app/hooks';
import { selectAffiliate } from '../../features/affiliate/affiliateSlice';

interface CheckOutProps {
  setSubmitting(v: boolean): void;
};


const CheckOut = (props: CheckOutProps, ref: any) => {
  const [nextClicked, setNextClicked] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [discountApplied, setDiscountApplied] = useState("");
  const [voucherCode, setVoucherCode] = useState('');
  const [discountData, setDiscountData] = useState(PURCHASE_PRICE);
  const stripe = useStripe();
  const elements = useElements();
  const affiliate = useAppSelector(selectAffiliate);

  useImperativeHandle(ref, () => ({
    async next() {
      if (accepted) {
        props.setSubmitting(true);
        const res = await handlePayment(null);
        props.setSubmitting(false);
        return res;
      } else {
        setNextClicked(true);
      }
    }
  }));

  useEffect(() => {
    applyAffilaiteDiscount();
  }, [])

  const applyVoucher = async () => {

    const voucherRes: any = await axios.get(SERVER_URL + "/validateVoucher", {
      params: { voucherCode: voucherCode },
      headers: { Authorization: await Authorization.AuthToken() }
    });

    if (voucherRes.status === 200 && voucherRes.data) {
      if (voucherRes.data.active) {
        var basePurcasePrice = parseFloat(PURCHASE_PRICE);

        setDiscountApplied(voucherRes.data.isFixedPrice
          ? `(discount £${voucherRes.data.value})`
          : `(discount ${voucherRes.data.value}%)`);

        var discountedValue = voucherRes.data.isFixedPrice
          ? (basePurcasePrice - voucherRes.data.value)
          : basePurcasePrice - (basePurcasePrice * voucherRes.data.value / 100)

        if (discountedValue < 0)
          discountedValue = 0;

        setDiscountData(discountedValue.toFixed(2));
      }
    }
  }

  const applyAffilaiteDiscount = async () => {
    if (affiliate && affiliate.discount > 0) {
      var basePurcasePrice = parseFloat(PURCHASE_PRICE);

      setDiscountApplied(`(discount ${affiliate.discount}%)`);

      var discountedValue = basePurcasePrice - (basePurcasePrice * affiliate.discount / 100)

      if (discountedValue < 0)
        discountedValue = 0;

      setDiscountData(discountedValue.toFixed(2));
    }
  }

  // When initial payment is not 0.00 pounds, payment should be confirmed.
  const confirmCardPayment = async (subscription: any) => {

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    const { client_secret } = subscription.latest_invoice.payment_intent;

    const result = await stripe.confirmCardPayment(client_secret, {
      payment_method: {
        card: elements.getElement(CardElement) as StripeCardElement,
        billing_details: {
          email: await Authorization.GetUsername(),
        },
      },
    });

    if (result.error) {
      console.log('[error]', result.error);
    } else {
      console.log('[PaymentIntent]', result.paymentIntent);
    }

    if (result.paymentIntent?.status === 'succeeded') {

      await axios.post(SERVER_URL + "/payment/syncSubscription", {}, {
        headers: {
          Authorization: await Authorization.AuthToken()
        }
      });

      return true;
    } else {
      alert('There is an issue with payment.')
      setNextClicked(false);
    }

    return false;
  }

  // When initial payment is 0.00 punds, only credit card setup should be confirmed.
  const confirmCardSetup = async (subscription: any) => {

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    const { pending_setup_intent } = subscription;

    if (pending_setup_intent) {
      const { client_secret, status } = subscription.pending_setup_intent;
      var result = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement) as StripeCardElement,
          billing_details: {
            email: await Authorization.GetUsername(),
          },
        }
      });

      if (result.error) {
        console.log('[error]', result.error);
      } else {
        console.log('[PaymentIntent]', result.setupIntent);
      }

      if (result.setupIntent?.status === 'succeeded') {
        await axios.post(SERVER_URL + "/payment/syncSubscription", {}, {
          headers: {
            Authorization: await Authorization.AuthToken()
          }
        });
                
        return true;
      }
    }

    return false;

  }

  const handlePayment = async (e: any) => {
    if (e)
      e.preventDefault();
    var subscriptionResponse: any = null;
    try {
      subscriptionResponse = await axios.post(SERVER_URL + "/payment/subscription", { voucherCode }, {
        headers: {
          Authorization: await Authorization.AuthToken()
        }
      });
    } catch (e) {
      console.log(e);
      alert('Please try again later.')
      setNextClicked(false);
      return;
    }

    if (subscriptionResponse.data.error) {
      alert('Please try again later.')
      setNextClicked(false);
      return;
    }

    if (subscriptionResponse.data.subscription.latest_invoice?.payment_intent?.client_secret != null) {
      return await confirmCardPayment(subscriptionResponse.data.subscription);
    } else {
      return await confirmCardSetup(subscriptionResponse.data.subscription);
    }
  };

  return (
    <form className="CheckOut" onSubmit={handlePayment} ref={ref}>
      <div className="bold ft-25">Let's get started</div>
      <div className="label-box ft-22">
        <div className="label">Capacity Vault secure setup:</div>
        <div className="bold">£{PURCHASE_PRICE}</div>
      </div>
      <div className="label-box ft-22">
        <div className="label">Subscription year 1:</div>
        <div className="bold">£0</div>
      </div>
      <div className="label-box ft-22">
        <div className="label">Subscription year 2 onwards:</div>
        <div className="bold">£10 per year</div>
      </div>
      <div className="bold ft-25 price-amount">Amount due : £{discountData} {discountApplied}</div>

      {!affiliate && <div className="flex-inline voucher-row">
        <input className="ft-18" onChange={e => setVoucherCode(e.target.value)} placeholder="voucher code?" />
        <button className="ft-18" onClick={e => {
          e.preventDefault();
          applyVoucher();
        }}>Apply</button>
      </div>}

      <div className={`flex-inline accept-condition-check ft-22 ${(nextClicked && !accepted) ? "invalid" : ''}`} onClick={() => setAccepted(!accepted)}>
        <Checkbox checked={accepted} />
        <div className="font-light">I agree to the <a className="blue" href={LANDING_URL + '/terms-and-conditions'} target="_blank">terms and conditions</a></div>
      </div>
      <div className="pay-card-row">
        <div className="card-box">
          <CardElement options={{ hidePostalCode: true }} />
        </div>
        <div className="desktop-inline-row">
          <img src="/PoweredbyStripe.svg" />
          <div className="bold">for secure payments</div>
        </div>
      </div>
      <div className="text-box ft-22">
        You can cancel this service at any time. If you cancel, you will have to pay to make new entries if you update your will, and your estate will have to pay a fee to access any recordings you make.
      </div>
    </form>
  )
};

export default forwardRef(CheckOut);
